import { useEffect, useRef } from 'react';

/**
 * Nền "chòm sao" của hero: các nút trôi chậm, nối nhau bằng vạch mờ, và cứ vài
 * giây một CẶP nút lóe sáng rồi tắt.
 *
 * Cái lóe sáng đó không phải trang trí ngẫu nhiên — nó là chính cơ chế bán hàng
 * của bot vẽ thành hình: trong dòng hội thoại của cộng đồng, bot tìm ra khoảnh
 * khắc một câu trả lời khớp với một câu hỏi. Người xem không cần biết điều đó
 * để thấy đẹp, nhưng người đọc kỹ trang sẽ nhận ra hình nền đang diễn lại đúng
 * điều khối "How the self-improving part actually works" mô tả.
 *
 * Canvas thuần, không thư viện: ~70 nút, phép so cặp O(n²) mỗi khung hình vẫn
 * dưới 5.000 phép — không đáng để kéo thêm một dependency animation vào build.
 *
 * Canvas không tự ăn theme như CSS, nên màu đọc từ token (--accent, --fg-subtle)
 * lúc gắn và đọc LẠI khi <html> đổi class dark — thiếu bước này thì bấm đổi
 * theme xong hình nền vẫn mang màu của theme cũ.
 *
 * `prefers-reduced-motion: reduce` → không vẽ gì cả: hero vẫn đứng vững chỉ với
 * chữ, và đây là khối duy nhất chuyển động liên tục trên trang.
 */

type Particle = { x: number; y: number; vx: number; vy: number; r: number };

/** Khoảng cách tối đa (px) để hai nút được nối vạch. */
const LINK_DIST = 130;
/** Mỗi khung hình cần bao nhiêu px² cho một nút — quyết định mật độ. */
const AREA_PER_PARTICLE = 15_000;
/** Nhịp lóe sáng của một cặp nút (ms giữa hai lần). */
const FLASH_EVERY = 2_600;
/** Một lần lóe kéo dài bao lâu (ms). */
const FLASH_LIFE = 1_000;

export function NeuralField(): React.JSX.Element {
  const ref = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let accent = '#6366f1';
    let dot = '#64748b';
    const readColors = (): void => {
      const style = getComputedStyle(document.documentElement);
      accent = style.getPropertyValue('--accent').trim() || accent;
      dot = style.getPropertyValue('--fg-subtle').trim() || dot;
    };
    readColors();
    const themeWatch = new MutationObserver(readColors);
    themeWatch.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    let particles: Particle[] = [];
    let width = 0;
    let height = 0;
    // Cặp đang lóe sáng: giữ THAM CHIẾU hạt chứ không giữ chỉ số — chỉ số vào
    // mảng bắt kiểm tra undefined ở từng chỗ đọc với `noUncheckedIndexedAccess`.
    let flash: { a: Particle; b: Particle; at: number } | null = null;

    const resize = (): void => {
      const parent = canvas.parentElement;
      if (!parent) return;
      const dpr = Math.min(window.devicePixelRatio, 2);
      width = parent.clientWidth;
      height = parent.clientHeight;
      canvas.width = Math.round(width * dpr);
      canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const target = Math.min(70, Math.round((width * height) / AREA_PER_PARTICLE));
      particles = Array.from({ length: target }, () => ({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.25,
        vy: (Math.random() - 0.5) * 0.25,
        r: 1 + Math.random() * 1.4,
      }));
      // Cặp đang lóe (nếu có) trỏ vào các hạt của lứa cũ — bỏ, không vẽ nốt.
      flash = null;
    };
    resize();
    const sizeWatch = new ResizeObserver(resize);
    if (canvas.parentElement) sizeWatch.observe(canvas.parentElement);

    let lastFlash = performance.now();

    let raf = 0;
    const tick = (now: number): void => {
      raf = requestAnimationFrame(tick);
      ctx.clearRect(0, 0, width, height);

      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        // Ra khỏi mép thì vòng lại mép kia — không nảy, nảy trông như bể cá.
        if (p.x < -5) p.x = width + 5;
        if (p.x > width + 5) p.x = -5;
        if (p.y < -5) p.y = height + 5;
        if (p.y > height + 5) p.y = -5;
      }

      // Vạch nối — độ mờ tỉ lệ nghịch với khoảng cách.
      ctx.strokeStyle = accent;
      ctx.lineWidth = 1;
      // Vòng theo chỉ số + chốt chặn undefined (noUncheckedIndexedAccess),
      // không slice() mảng con: hàm này chạy 60 lần mỗi giây.
      for (let i = 0; i < particles.length; i++) {
        const a = particles[i];
        if (a === undefined) continue;
        for (let j = i + 1; j < particles.length; j++) {
          const b = particles[j];
          if (b === undefined) continue;
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d > LINK_DIST) continue;
          ctx.globalAlpha = (1 - d / LINK_DIST) * 0.18;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }

      // Nút.
      ctx.fillStyle = dot;
      ctx.globalAlpha = 0.5;
      for (const p of particles) {
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      // Chọn một cặp mới để lóe khi cặp cũ đã tắt và đủ nhịp chờ.
      if (flash === null && now - lastFlash > FLASH_EVERY && particles.length > 1) {
        const origin = particles[Math.floor(Math.random() * particles.length)];
        if (origin !== undefined) {
          // Tìm nút gần nhất trong tầm nối — lóe một cặp KHÔNG có vạch nối
          // sẵn thì trông như hai đèn chớp vô can, mất luôn cái ý "ghép cặp".
          let best: Particle | null = null;
          let bestD = LINK_DIST;
          for (const other of particles) {
            if (other === origin) continue;
            const d = Math.hypot(origin.x - other.x, origin.y - other.y);
            if (d < bestD) {
              bestD = d;
              best = other;
            }
          }
          if (best !== null) flash = { a: origin, b: best, at: now };
        }
        lastFlash = now;
      }

      if (flash !== null) {
        const t = (now - flash.at) / FLASH_LIFE;
        if (t >= 1) {
          flash = null;
        } else {
          // Sáng lên nhanh rồi tắt chậm: sin cho nửa chu kỳ là đủ.
          const glow = Math.sin(t * Math.PI);
          const { a, b } = flash;
          ctx.strokeStyle = accent;
          ctx.fillStyle = accent;
          ctx.lineWidth = 1.5;
          ctx.globalAlpha = glow * 0.9;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
          for (const p of [a, b]) {
            ctx.beginPath();
            ctx.arc(p.x, p.y, p.r + glow * 2.5, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }

      ctx.globalAlpha = 1;
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      themeWatch.disconnect();
      sizeWatch.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      // Mờ dần về đáy để hero nối êm vào khối bên dưới thay vì cắt phựt.
      className="pointer-events-none absolute inset-0 -z-10 [mask-image:linear-gradient(to_bottom,black_55%,transparent)]"
    />
  );
}
